import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import {
  BookOpen,
  Boxes,
  FolderTree,
  Library,
  ListTree,
  Ruler,
  Search,
  Inbox,
  GitBranch,
  Gauge,
  Tags,
} from "lucide-react";
import { useGetMyFeatures } from "@tubo/api-models/hooks/tenant-feature-api.hooks";
import {
  SecondaryNav,
  useSecondaryNavNavHeader,
} from "@client/components/secondary-nav";
import type { SecondaryNavItem } from "@client/components/secondary-nav";
import { Button } from "@tubo/ui/components/ui/button";
import { useNavPermissions } from "@client/hooks/use-nav-permissions";
import { NamespaceSwitcher } from "@client/components/semantic-platform/namespace-switcher";

export const Route = createFileRoute("/_app/semantic-platform")({
  component: SemanticPlatformLayout,
});

type FeatureKey = "dataModel" | "semanticAsset" | "run" | "assetPublishing" | "structuredKnowledge";

/** 域级门禁:五个开关任一开启即可进入(叶子门禁由二级目录逐项过滤) */
const SEMANTIC_FEATURES: FeatureKey[] = [
  "dataModel",
  "semanticAsset",
  "run",
  "assetPublishing",
  "structuredKnowledge",
];

/**
 * 知识本体二级目录（三层心智：知识本体 → 领域 → 领域内容）：
 * - 领域上下文由标题行下方的切换器指示，全部条目均跟随其过滤；
 * - 按工作流职能分节：语义定义（有什么）→ 建模与加工（怎么落）→ 资产与治理（产出与质量）→ 标签中心（打标与审核）；
 * - 「领域管理」固定在左栏底部，不随菜单滚动。
 * 叶子项按租户功能开关与静态权限过滤。
 */
const ONTOLOGY_NAV: Array<SecondaryNavItem & { feature?: FeatureKey; permission?: string }> = [
  { title: "语义定义", kind: "section" },
  { title: "概念管理", to: "/semantic-platform/concepts", icon: BookOpen, feature: "semanticAsset", permission: "semantic-concepts" },
  { title: "数据字典", to: "/semantic-platform/vocabularies", icon: ListTree, feature: "semanticAsset", permission: "semantic-vocabularies" },
  { title: "度量与单位", to: "/semantic-platform/quantities", icon: Ruler, feature: "semanticAsset", permission: "semantic-quantities" },
  { title: "建模与加工", kind: "section" },
  { title: "数据模型", to: "/semantic-platform/data-models", icon: Boxes, feature: "dataModel", permission: "semantic-data-model" },
  { title: "资产与治理", kind: "section" },
  { title: "数据资产", to: "/semantic-platform/data-assets", icon: Library, feature: "assetPublishing", permission: "data-asset" },
  { title: "分析中心", to: "/semantic-platform/data-governance/analysis", icon: Gauge, feature: "semanticAsset" },
  { title: "数据血缘", to: "/semantic-platform/data-lineage", icon: GitBranch, feature: "semanticAsset", permission: "data-governance-impact" },
  { title: "知识调试", to: "/semantic-platform/knowledge-debug", icon: Search, feature: "structuredKnowledge", permission: "knowledge-debug" },
  { title: "标签中心", kind: "section" },
  { title: "标签字段", to: "/semantic-platform/tag-center/fields", icon: Tags, feature: "semanticAsset", permission: "data-governance-classification" },
  { title: "建议审核", to: "/semantic-platform/tag-center/suggestions", icon: Inbox, feature: "semanticAsset", permission: "data-governance-suggestion" },
  { title: "领域管理", to: "/semantic-platform/namespaces", icon: FolderTree, feature: "semanticAsset", permission: "semantic-glossary", pinnedBottom: true },
];

/**
 * 语义数据平台路由级 Feature 门禁(评审验收 P1-6)+ 二级目录布局(two-level-nav 规范)
 *
 * 未开启任何 semanticData.* 开关的租户:侧栏隐藏 + 直接输入 URL 也被拦截;
 * 部分开启:进入域,二级目录仅显示已开启分节的叶子项。
 */

function SemanticPlatformLayout() {
  const navigate = useNavigate();
  const { data: features, isLoading } = useGetMyFeatures({ query: {} });
  const location = useLocation();
  // 模型设计器为沉浸式单页(6A-1):编辑路由隐藏二级目录,画布占满全部内容区
  // 模型详情同为全屏形态:页内自渲染 header
  const immersive =
    location.pathname.startsWith("/semantic-platform/data-models/edit") ||
    /^\/semantic-platform\/data-models\/(?!edit\b)[^/]+$/.test(location.pathname);

  // 领域切换器：模块级上下文，常驻标题行下方（第二层=领域的心智锚点）
  useSecondaryNavNavHeader(<NamespaceSwitcher />);

  // 后端一次返回当前用户有权访问的所有 key（hooks 必须在早期 return 之前调用）
  const permSet = useNavPermissions();

  if (isLoading) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">加载中…</div>;
  }

  if (!SEMANTIC_FEATURES.some((key) => features?.[key] === true)) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Boxes className="mx-auto mb-3 h-12 w-12 text-icon-secondary-muted" />
          <p className="text-sm font-medium">知识本体</p>
          <p className="mt-1 text-xs text-muted-foreground/70">该功能尚未对当前租户开放</p>
          {/* 直链进入时浏览器历史可能是空/站外，域级门禁无域内落点，固定回工作台 */}
          <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate({ to: "/workbench" })}>
            返回
          </Button>
        </div>
      </div>
    );
  }

  if (immersive) {
    // 隐藏二级目录但保留 header:设计器工具条经 useSecondaryNavTitle/Actions 注入 SecondaryNav,
    // 完全跳过会导致保存/校验/冻结/发布按钮无处渲染(修复:裸 Outlet 改为 hideNav 形态)
    return (
      <SecondaryNav title="知识本体" items={[]} hideNav>
        <Outlet />
      </SecondaryNav>
    );
  }

  const items = ONTOLOGY_NAV
    .filter((item) => !item.feature || features?.[item.feature] === true)
    .filter((item) => {
      if (!item.permission) return true;
      return permSet.has(item.permission);
    });

  return (
    <SecondaryNav title="知识本体" items={items}>
      <Outlet />
    </SecondaryNav>
  );
}
